import { useState, useEffect } from 'react'
import {
  Shield,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Link2,
  FileText,
  Plane,
  Calendar,
  ClipboardList,
  Wrench,
  Eye,
  EyeOff,
  ExternalLink,
  Lock,
  RefreshCw,
  Info,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import api from '@/services/api'

// ── Interfaces ──────────────────────────────────────────────────────────────

type ConnectionStatus = 'not_connected' | 'connected' | 'error'

interface CampCredentials {
  username: string
  password: string
  operatorCode: string
}

interface CampDueItem {
  id: string
  tailNumber: string
  taskDescription: string
  ataChapter: string
  dueDate: string
  dueHours: number
  remainingHours: number
  priority: 'routine' | 'priority' | 'urgent'
}

interface CampAircraftStatus {
  tailNumber: string
  model: string
  totalHours: number
  totalCycles: number
  complianceStatus: 'compliant' | 'non-compliant' | 'pending'
  openItems: number
}

// ── Sample Preview Data ─────────────────────────────────────────────────────

const previewDueItems: CampDueItem[] = [
  {
    id: '1',
    tailNumber: 'N420HJ',
    taskDescription: 'Phase 4 Inspection — Avionics, Electrical, Completion',
    ataChapter: 'ATA 22/24/34',
    dueDate: '2026-04-15',
    dueHours: 600,
    remainingHours: 112.7,
    priority: 'routine',
  },
  {
    id: '2',
    tailNumber: 'N421HA',
    taskDescription: 'Phase 2 Inspection — GE Honda HF120 Engine',
    ataChapter: 'ATA 72/73/79',
    dueDate: '2026-03-05',
    dueHours: 150,
    remainingHours: 7.2,
    priority: 'urgent',
  },
  {
    id: '3',
    tailNumber: 'N523HE',
    taskDescription: 'AD 2025-18-06 — Fuel Quantity Indication System',
    ataChapter: 'ATA 28',
    dueDate: '2026-03-30',
    dueHours: 300,
    remainingHours: 8.6,
    priority: 'priority',
  },
]

const previewAircraftStatus: CampAircraftStatus[] = [
  { tailNumber: 'N420HJ', model: 'HA-420 Elite S', totalHours: 487.3, totalCycles: 612, complianceStatus: 'compliant', openItems: 2 },
  { tailNumber: 'N421HA', model: 'HA-420 Elite II', totalHours: 142.8, totalCycles: 198, complianceStatus: 'pending', openItems: 5 },
  { tailNumber: 'N523HE', model: 'HA-420 Elite', totalHours: 291.4, totalCycles: 345, complianceStatus: 'non-compliant', openItems: 3 },
]

// ── Component ───────────────────────────────────────────────────────────────

export function CampIntegrationPage() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('not_connected')
  const [credentials, setCredentials] = useState<CampCredentials>({
    username: '',
    password: '',
    operatorCode: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const data = await api.get<{ status: ConnectionStatus }>('/api/integrations/camp/status')
        if (data?.status) {
          setConnectionStatus(data.status)
        }
      } catch {
        // Not connected by default
      }
    }
    checkConnection()
  }, [])

  const handleConnect = async () => {
    if (!credentials.username || !credentials.password || !credentials.operatorCode) {
      setError('All fields are required.')
      return
    }

    setConnecting(true)
    setError(null)

    try {
      const result = await api.post<{ status: ConnectionStatus; message?: string }>(
        '/api/integrations/camp/configure',
        {
          username: credentials.username,
          password: credentials.password,
          operatorCode: credentials.operatorCode,
        }
      )
      if (result?.status === 'connected') {
        setConnectionStatus('connected')
      } else {
        setConnectionStatus('error')
        setError(result?.message || 'Connection failed. Please verify your credentials.')
      }
    } catch {
      setConnectionStatus('error')
      setError('Unable to connect to CAMP Systems. Please verify your credentials and try again.')
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      await api.post('/api/integrations/camp/disconnect')
    } catch {
      // Continue with local state update
    }
    setConnectionStatus('not_connected')
    setCredentials({ username: '', password: '', operatorCode: '' })
  }

  const getStatusIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
            <span className="font-medium text-green-700">Connected</span>
          </div>
        )
      case 'error':
        return (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500" />
            <span className="font-medium text-red-700">Error</span>
          </div>
        )
      case 'not_connected':
      default:
        return (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-gray-400" />
            <span className="font-medium text-gray-600">Not Connected</span>
          </div>
        )
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'priority':
        return 'bg-orange-100 text-orange-800'
      case 'routine':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getComplianceBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return <Badge variant="success">Compliant</Badge>
      case 'non-compliant':
        return <Badge variant="destructive">Non-Compliant</Badge>
      case 'pending':
        return <Badge variant="warning">Pending</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CAMP Systems Integration</h1>
          <p className="text-gray-600">Connect Voltaire to CAMP for maintenance tracking and compliance</p>
        </div>
        {getStatusIndicator()}
      </div>

      {/* Hero Card */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-blue-600 p-3 shrink-0">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">CAMP Systems</h2>
                <p className="mt-1 text-gray-600 max-w-xl">
                  CAMP Systems is the industry-standard maintenance tracking platform used by over 19,000
                  aircraft worldwide. HondaJet is a factory-recommended CAMP provider. CAMP tracks ADs,
                  service bulletins, scheduled maintenance, and ensures continuous airworthiness compliance
                  for the HA-420 fleet.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="info">Factory Recommended</Badge>
                  <Badge variant="outline">19,000+ Aircraft Tracked</Badge>
                  <Badge variant="outline">FAA Part 91/135 Compliant</Badge>
                </div>
              </div>
            </div>
            <div className="shrink-0">
              <a
                href="https://www.campsystems.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                campsystems.com
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connection Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Connection Settings
          </CardTitle>
          <CardDescription>
            Enter your CAMP Systems credentials to establish the connection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connectionStatus === 'connected' ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
                <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-medium text-green-900">Connected to CAMP Systems</p>
                  <p className="text-sm text-green-700">
                    Operator Code: {credentials.operatorCode || '***'}
                    {' | '}Last verified: {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">CAMP Username</label>
                  <Input
                    placeholder="camp_username"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">CAMP Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="password"
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Operator Code</label>
                  <Input
                    placeholder="HAC001"
                    value={credentials.operatorCode}
                    onChange={(e) => setCredentials({ ...credentials, operatorCode: e.target.value })}
                  />
                </div>
              </div>
              <Button
                onClick={handleConnect}
                disabled={connecting || !credentials.username || !credentials.password || !credentials.operatorCode}
              >
                {connecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="mr-2 h-4 w-4" />
                    Connect CAMP
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Features — Coming Soon */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Integration Features</h2>
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">Coming Soon</Badge>
        </div>
        <p className="mb-4 text-sm text-gray-500">
          Coming Soon — Available after NDA completion with CAMP Systems
        </p>

        <div className="grid gap-4 md:grid-cols-3">
          {/* Due List Sync */}
          <Card className="relative overflow-hidden opacity-75">
            <div className="absolute right-3 top-3">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Coming Soon</Badge>
            </div>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-blue-100 p-2">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle className="text-base">Due List Sync</CardTitle>
              </div>
              <CardDescription>
                Automatically import upcoming due items from CAMP into Voltaire for work package planning.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4" />
                <span>Real-time synchronization</span>
              </div>
            </CardContent>
          </Card>

          {/* Aircraft Status Sync */}
          <Card className="relative overflow-hidden opacity-75">
            <div className="absolute right-3 top-3">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Coming Soon</Badge>
            </div>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-green-100 p-2">
                  <Plane className="h-5 w-5 text-green-600" />
                </div>
                <CardTitle className="text-base">Aircraft Status Sync</CardTitle>
              </div>
              <CardDescription>
                Pull aircraft hours, cycles, and compliance status directly from CAMP into your fleet dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CheckCircle2 className="h-4 w-4" />
                <span>Compliance tracking</span>
              </div>
            </CardContent>
          </Card>

          {/* Work Order Integration */}
          <Card className="relative overflow-hidden opacity-75">
            <div className="absolute right-3 top-3">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Coming Soon</Badge>
            </div>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-purple-100 p-2">
                  <Wrench className="h-5 w-5 text-purple-600" />
                </div>
                <CardTitle className="text-base">Work Order Integration</CardTitle>
              </div>
              <CardDescription>
                Generate work orders in Voltaire and push sign-off data back to CAMP for compliance records.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Lock className="h-4 w-4" />
                <span>Requires CAMP API NDA</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Cards */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Preview: What CAMP Integration Provides</h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upcoming Due Items Preview */}
          <Card className="relative">
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
              <div className="rounded-lg bg-white border shadow-lg px-6 py-4 text-center">
                <Lock className="mx-auto h-6 w-6 text-gray-400 mb-2" />
                <p className="font-medium text-gray-700">Coming Soon</p>
                <p className="text-xs text-gray-500">Available after NDA completion</p>
              </div>
            </div>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Upcoming Due Items from CAMP
              </CardTitle>
              <CardDescription>Preview of maintenance items pulled from CAMP due list</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {previewDueItems.map((item) => (
                  <div key={item.id} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-900">{item.taskDescription}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-mono">{item.tailNumber}</span>
                          <span className="text-gray-300">|</span>
                          <span>{item.ataChapter}</span>
                        </div>
                      </div>
                      <Badge className={getPriorityColor(item.priority)}>{item.priority}</Badge>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>Due: {item.dueDate} ({item.dueHours} hrs)</span>
                      <span className={item.remainingHours <= 10 ? 'font-semibold text-red-600' : ''}>
                        {item.remainingHours} hrs remaining
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Aircraft Compliance Status Preview */}
          <Card className="relative">
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
              <div className="rounded-lg bg-white border shadow-lg px-6 py-4 text-center">
                <Lock className="mx-auto h-6 w-6 text-gray-400 mb-2" />
                <p className="font-medium text-gray-700">Coming Soon</p>
                <p className="text-xs text-gray-500">Available after NDA completion</p>
              </div>
            </div>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                Aircraft Compliance Status
              </CardTitle>
              <CardDescription>Preview of fleet compliance data from CAMP</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {previewAircraftStatus.map((aircraft) => (
                  <div key={aircraft.tailNumber} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Plane className="h-4 w-4 text-gray-400" />
                        <div>
                          <span className="font-mono font-semibold text-gray-900">{aircraft.tailNumber}</span>
                          <span className="ml-2 text-sm text-gray-500">{aircraft.model}</span>
                        </div>
                      </div>
                      {getComplianceBadge(aircraft.complianceStatus)}
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span>{aircraft.totalHours.toFixed(1)} hrs</span>
                      <span>{aircraft.totalCycles} cycles</span>
                      <span>{aircraft.openItems} open items</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Automatic Work Package Generation Preview */}
      <Card className="relative">
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
          <div className="rounded-lg bg-white border shadow-lg px-6 py-4 text-center">
            <Lock className="mx-auto h-6 w-6 text-gray-400 mb-2" />
            <p className="font-medium text-gray-700">Coming Soon</p>
            <p className="text-xs text-gray-500">Available after NDA completion</p>
          </div>
        </div>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-5 w-5 text-purple-500" />
            Automatic Work Package Generation
          </CardTitle>
          <CardDescription>
            Voltaire will generate work packages from CAMP due items, pulling relevant manual references,
            inspection cards, and parts lists automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border bg-gray-50 p-4 text-center text-sm text-gray-500">
            <FileText className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p>Work packages will be auto-generated from CAMP due items</p>
            <p className="text-xs mt-1">Including manual references, parts lists, and inspection cards from Voltaire's knowledge base</p>
          </div>
        </CardContent>
      </Card>

      {/* NDA Notice */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 text-amber-600 shrink-0" />
            <div>
              <p className="font-medium text-amber-900">CAMP API Integration Requires NDA</p>
              <p className="text-sm text-amber-700">
                Full CAMP Systems API access requires a signed Non-Disclosure Agreement with CAMP Systems International.
                Contact your Voltaire Aviation representative or CAMP directly at{' '}
                <a
                  href="https://www.campsystems.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-amber-900"
                >
                  campsystems.com/contact
                </a>
                {' '}to begin the integration process. The credential-based connection above provides basic status verification.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
